import { AnimatePresence } from 'motion/react';
import React, { Suspense } from 'react';
import { useLocation, useOutlet } from 'react-router-dom';

import { LoadingSpinner } from '@core/components/LoadingSpinner';
import { DashboardSidebar } from '@core/components/navigation/DashboardSidebar';

const AnimatedOutlet = () => {
  const location = useLocation();
  const element = useOutlet();

  return (
    <AnimatePresence mode="wait" initial={true}>
      {element && React.cloneElement(element, { key: location.pathname.split('/')[1] })}
    </AnimatePresence>
  );
};

/**
 * DashboardLayout provides the main application structure for authenticated users.
 *
 * Use this layout for:
 * - Protected routes that require authentication
 * - Application features (settings, dashboard, etc.)
 * - Any page that needs access to the main navigation and app shell
 *
 * Features:
 * - Main navigation
 * - User menu
 * - Sidebar (if applicable)
 * - Proper spacing for application content
 * - Protected route wrapper
 *
 * Note: This layout should always be used with RouteGuard mode="protected" since it needs the user data to be available.
 * @example
 * {
 *   element: (
 *     <RouteGuard mode="protected">
 *       <DashboardLayout />
 *     </RouteGuard>
 *   )
 * }
 */
export default function DashboardLayout() {
  return (
    <DashboardSidebar>
      <Suspense
        fallback={
          <div className="flex h-screen w-full items-center justify-center">
            <LoadingSpinner />
          </div>
        }
      >
        <AnimatedOutlet />
      </Suspense>
    </DashboardSidebar>
  );
}
